import React, { useRef, useState, useContext, useEffect } from "react";
import Swal from "sweetalert2";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import Templete from "../Templete/Templete";
import AuthService from "../../../services/Admin/AuthService";
import UserService from "../../../services/Admin/UserService";
import PackageService from "../../../services/Admin/PackageService";
import HttpError from "../../../utill/HttpError";
import LoadingContext from "../../../store/loading-context";
import { states } from "../../../utill/Common";

const AddUser = () => {
  const loadingCtx = useContext(LoadingContext);
  const [sessionUserType, setSessionUserType] = useState("");

  // Form state management
  const [formData, setFormData] = useState({
    userType: "",
    domainName: "",
    name: "",
    shopName: "",
    mobile: "",
    email: "",
    pincode: "",
    state: "",
    city: "",
    district: "",
    address: "",
  });

  const [nsdlPANStatus, setNSDLPANStatus] = useState(null);
  const [packageId, setPackageId] = useState(null);
  const [location, setLocation] = useState(null);
  const [packageList, setPackageList] = useState([]);
  const formRef = useRef();

  useEffect(() => {
    getUserType();
    fetchPackageList();
  }, []);

  const getUserType = () => {
    const _userType = AuthService.getUserType();
    setSessionUserType(_userType);
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleUserTypeChange = (value) => {
    handleInputChange("userType", value);
    // Clear domain name when user type changes (if not reseller/master reseller)
    if (value !== "Reseller" && value !== "Master Reseller") {
      handleInputChange("domainName", "");
    }
  };

  const handlePincodeChange = async (pincode) => {
    handleInputChange("pincode", pincode);

    if (pincode.length === 6 && /^\d+$/.test(pincode)) {
      await getLocationHandler(pincode);
    } else if (pincode.length === 0) {
      // Reset location data when pincode is cleared
      setLocation(null);
      handleInputChange("state", "");
      handleInputChange("city", "");
      handleInputChange("district", "");
    }
  };

  const validateForm = () => {
    const errors = [];

    // User Type validation
    if (!formData.userType) {
      errors.push("User Type is required");
    }

    // Domain name validation for Reseller/Master Reseller
    if (
      formData.userType === "Reseller" ||
      formData.userType === "Master Reseller"
    ) {
      if (!formData.domainName || formData.domainName.trim() === "") {
        errors.push("Domain Name is required for Reseller/Master Reseller");
      }
    }

    // Name validation
    if (!formData.name || formData.name.trim() === "") {
      errors.push("Name is required");
    }

    // Shop Name validation
    if (!formData.shopName || formData.shopName.trim() === "") {
      errors.push("Business/Shop Name is required");
    }

    // Mobile validation
    if (!formData.mobile) {
      errors.push("Mobile Number is required");
    } else if (!/^\d{10}$/.test(formData.mobile)) {
      errors.push("Mobile Number must be 10 digits");
    }

    // Email validation
    if (!formData.email) {
      errors.push("Email ID is required");
    } else {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email)) {
        errors.push("Invalid Email ID format");
      }
    }

    // Pincode validation
    if (!formData.pincode) {
      errors.push("PIN Code is required");
    } else if (!/^\d{6}$/.test(formData.pincode)) {
      errors.push("PIN Code must be 6 digits");
    }

    // State validation
    if (!formData.state) {
      errors.push("State is required");
    }

    // City validation
    if (!formData.city || formData.city.trim() === "") {
      errors.push("City is required");
    }

    // District validation
    if (!formData.district || formData.district.trim() === "") {
      errors.push("District is required");
    }

    // Address validation
    if (!formData.address || formData.address.trim() === "") {
      errors.push("Address is required");
    }

    // Show all errors at once
    if (errors.length > 0) {
      Swal.fire({
        icon: "error",
        title: "Validation Error",
        html: errors.map((error) => `<div>• ${error}</div>`).join(""),
        confirmButtonText: "OK",
      });
      return false;
    }

    return true;
  };

  const addUserHandler = async () => {
    if (!validateForm()) {
      return;
    }

    loadingCtx.startLoading();

    try {
      const token = AuthService.getAccessToken();
      const userData = {
        userType: formData.userType,
        domainName: formData.domainName.trim(),
        packageId,
        panNumber: "",
        name: formData.name.trim(),
        shopName: formData.shopName.trim(),
        dob: "",
        gender: "",
        mobile: formData.mobile,
        email: formData.email.trim(),
        pincode: formData.pincode,
        state: formData.state,
        city: formData.city.trim(),
        district: formData.district.trim(),
        address: formData.address.trim(),
        nsdlpan_status: nsdlPANStatus || null,
      };

      const responseData = await UserService.add(token, userData);

      if (responseData.status === "SUCCESS") {
        resetForm();
        Swal.fire("Success!", responseData.message, "success");
      } else {
        Swal.fire("Oops!", responseData.message, "error");
      }
    } catch (error) {
      const errMsg = HttpError.getError(error);
      Swal.fire("Oops!", errMsg, "error");
    } finally {
      loadingCtx.stopLoading();
    }
  };

  const resetForm = () => {
    if (formRef.current) {
      formRef.current.reset();
    }
    setPackageId(null);
    setLocation(null);
    setFormData({
      userType: "",
      domainName: "",
      name: "",
      shopName: "",
      mobile: "",
      email: "",
      pincode: "",
      state: "",
      city: "",
      district: "",
      address: "",
    });
  };

  const fetchPackageList = async () => {
    loadingCtx.startLoading();
    try {
      const token = AuthService.getAccessToken();
      const responseData = await PackageService.fetchAll(token);

      if (responseData.status === "SUCCESS") {
        setPackageList(responseData.data || []);
      } else {
        Swal.fire("Oops!", responseData.message, "error");
      }
    } catch (error) {
      const errMsg = HttpError.getError(error);
      Swal.fire("Oops!", errMsg, "error");
    } finally {
      loadingCtx.stopLoading();
    }
  };

  const getLocationHandler = async (pincode) => {
    if (!pincode || pincode.length !== 6) return;

    loadingCtx.startLoading();
    try {
      const token = AuthService.getAccessToken();
      const responseData = await UserService.getLocation(token, pincode);

      if (
        responseData.status === "SUCCESS" &&
        responseData.data &&
        responseData.data[0]
      ) {
        const locationData = responseData.data[0];
        setLocation(locationData);

        // Update form data with location info
        setFormData((prev) => ({
          ...prev,
          state: locationData.Circle || "",
          city: locationData.Region || "",
          district: locationData.District || "",
        }));
      } else {
        Swal.fire("Info", "Location not found. Please enter manually.", "info");
      }
    } catch (error) {
      const errMsg = HttpError.getError(error);
      Swal.fire("Oops!", errMsg, "error");
    } finally {
      loadingCtx.stopLoading();
    }
  };

  return (
    <Templete t1="Home" t2="Add User">
      <section className="content">
        <div className="container-fluid">
          <div className="row">
            <div className="col-md-12">
              <div className="card card-primary">
                <div className="card-header">
                  <h3 className="card-title">Add User</h3>
                </div>
                <div className="row">
                  <div className="col-md-6 mx-auto">
                    <form ref={formRef} autoComplete="off">
                      <div className="card-body mt-2 mb-2">
                        {/* User Type - Required */}
                        <div className="form-group row">
                          <label className="col-sm-3 col-form-label required">
                            User Type
                          </label>
                          <div className="col-sm-9">
                            <select
                              className="form-control"
                              value={formData.userType}
                              onChange={(e) =>
                                handleUserTypeChange(e.target.value)
                              }
                              required
                            >
                              <option value="">---Select User Type---</option>
                              <option value="Retailer">Retailer</option>
                              <option value="Distributor">Distributor</option>
                              <option value="Master Distributor">
                                Master Distributor
                              </option>
                              <option value="Reseller">Reseller</option>
                              <option value="Master Reseller">
                                Master Reseller
                              </option>
                            </select>
                          </div>
                        </div>

                        {/* Domain Name - Required for Reseller/Master Reseller */}
                        {(formData.userType === "Reseller" ||
                          formData.userType === "Master Reseller") && (
                          <div className="form-group row">
                            <label className="col-sm-3 col-form-label required">
                              Domain Name
                            </label>
                            <div className="col-sm-9">
                              <input
                                type="text"
                                className="form-control"
                                placeholder="Domain Name"
                                value={formData.domainName}
                                onChange={(e) =>
                                  handleInputChange(
                                    "domainName",
                                    e.target.value,
                                  )
                                }
                                required={
                                  formData.userType === "Reseller" ||
                                  formData.userType === "Master Reseller"
                                }
                              />
                            </div>
                          </div>
                        )}

                        {/* Package Selection - Optional */}
                        <div className="form-group row">
                          <label className="col-sm-3 col-form-label">
                            Select Package
                          </label>
                          <div className="col-sm-9">
                            <Autocomplete
                              options={packageList}
                              getOptionLabel={(option) => option.name || ""}
                              value={
                                packageList.find(
                                  (pkg) => pkg.id === packageId,
                                ) || null
                              }
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  label="Select Package (Optional)"
                                />
                              )}
                              onChange={(e, v) => {
                                setPackageId(v ? v.id : null);
                              }}
                              isOptionEqualToValue={(option, value) =>
                                option.id === value.id
                              }
                            />
                            <small className="text-muted">
                              Optional - can be assigned later
                            </small>
                          </div>
                        </div>

                        {/* Shop Name - Required */}
                        <div className="form-group row">
                          <label className="col-sm-3 col-form-label required">
                            Business/Shop Name
                          </label>
                          <div className="col-sm-9">
                            <input
                              type="text"
                              className="form-control"
                              placeholder="Business/Shop Name"
                              value={formData.shopName}
                              onChange={(e) =>
                                handleInputChange("shopName", e.target.value)
                              }
                              required
                            />
                          </div>
                        </div>

                        {/* Name - Required */}
                        <div className="form-group row">
                          <label className="col-sm-3 col-form-label required">
                            Name
                          </label>
                          <div className="col-sm-9">
                            <input
                              type="text"
                              className="form-control"
                              placeholder="Name"
                              value={formData.name}
                              onChange={(e) =>
                                handleInputChange("name", e.target.value)
                              }
                              required
                            />
                          </div>
                        </div>

                        {/* Mobile Number - Required */}
                        <div className="form-group row">
                          <label className="col-sm-3 col-form-label required">
                            Mobile Number
                          </label>
                          <div className="col-sm-9">
                            <input
                              type="text"
                              className="form-control"
                              placeholder="Mobile Number"
                              maxLength="10"
                              value={formData.mobile}
                              onChange={(e) =>
                                handleInputChange(
                                  "mobile",
                                  e.target.value.replace(/[^0-9]/g, ""),
                                )
                              }
                              required
                            />
                          </div>
                        </div>

                        {/* Email - Required */}
                        <div className="form-group row">
                          <label className="col-sm-3 col-form-label required">
                            Email ID
                          </label>
                          <div className="col-sm-9">
                            <input
                              type="email"
                              className="form-control"
                              placeholder="Email ID"
                              value={formData.email}
                              onChange={(e) =>
                                handleInputChange("email", e.target.value)
                              }
                              required
                            />
                          </div>
                        </div>

                        {/* Pincode - Required */}
                        <div className="form-group row">
                          <label className="col-sm-3 col-form-label required">
                            PIN Code
                          </label>
                          <div className="col-sm-9">
                            <input
                              type="text"
                              className="form-control"
                              placeholder="PIN Code"
                              maxLength="6"
                              value={formData.pincode}
                              onChange={(e) =>
                                handlePincodeChange(
                                  e.target.value.replace(/[^0-9]/g, ""),
                                )
                              }
                              required
                            />
                          </div>
                        </div>

                        {/* State - Required */}
                        <div className="form-group row">
                          <label className="col-sm-3 col-form-label required">
                            State
                          </label>
                          <div className="col-sm-9">
                            <select
                              className="form-control"
                              value={formData.state}
                              onChange={(e) =>
                                handleInputChange("state", e.target.value)
                              }
                              required
                            >
                              <option value="">---Select State---</option>
                              {states.map((stateOption, index) => (
                                <option key={index} value={stateOption.value}>
                                  {stateOption.label}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>

                        {/* City - Required */}
                        <div className="form-group row">
                          <label className="col-sm-3 col-form-label required">
                            City
                          </label>
                          <div className="col-sm-9">
                            <input
                              type="text"
                              className="form-control"
                              placeholder="City"
                              value={formData.city}
                              onChange={(e) =>
                                handleInputChange("city", e.target.value)
                              }
                              required
                            />
                          </div>
                        </div>

                        {/* District - Required */}
                        <div className="form-group row">
                          <label className="col-sm-3 col-form-label required">
                            District
                          </label>
                          <div className="col-sm-9">
                            <input
                              type="text"
                              className="form-control"
                              placeholder="District"
                              value={formData.district}
                              onChange={(e) =>
                                handleInputChange("district", e.target.value)
                              }
                              required
                            />
                          </div>
                        </div>

                        {/* Address - Required */}
                        <div className="form-group row">
                          <label className="col-sm-3 col-form-label required">
                            Address
                          </label>
                          <div className="col-sm-9">
                            <textarea
                              className="form-control"
                              placeholder="Address"
                              rows="3"
                              value={formData.address}
                              onChange={(e) =>
                                handleInputChange("address", e.target.value)
                              }
                              required
                            />
                          </div>
                        </div>

                        <div className="form-group row">
                          <label className="col-sm-3 col-form-label required">
                            NSDL PAN Status
                          </label>
                          <div className="col-sm-9">
                            <select
                              className="form-control"
                              value={nsdlPANStatus || ""}
                              onChange={(e) => setNSDLPANStatus(e.target.value)}
                              required
                            >
                              <option value="">---Select Status---</option>
                              <option value="ENABLED">ENABLED</option>
                              <option value="DISABLED">DISABLED</option>
                            </select>
                          </div>
                        </div>

                        {/* Buttons */}
                        <div className="text-center">
                          <button
                            type="button"
                            className="btn btn-primary"
                            onClick={addUserHandler}
                          >
                            Submit
                          </button>
                          <button
                            type="button"
                            className="btn btn-default ml-5"
                            onClick={resetForm}
                          >
                            Reset
                          </button>
                        </div>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Templete>
  );
};

export default AddUser;
